import { TemplateConfig } from "@/interfaces/editor";
import { SyncStatus } from "./SyncStatus";
import { UserDevice } from "./UserDevice";
import { Project } from "./Project";
import { DateProvider } from "@/shared/DateProvider";
import { SupportedFeature, SupportedFeatureOnWebValues } from "../SupportedFeature";
import { reviveWithProto } from "@/shared/revive";
import { CanvasCompositionMode } from "@/scenes/engine/common/constants";

export class DraftSyncVersion {
    type = "DraftSyncVersion"
    
    draftId: string;
    createDate: Date;
    size: { width: number, height: number };
    canvasSizeIsCircular: boolean;
    duration: number;
    scenes: number;
    minVersion: number;
    title: string;
    customFonts: string[];
    isInConflict: boolean;
    updateDeviceId: string;
    screenshotUrl?: string;
    features: SupportedFeature[] = [];
    canvasComposition: CanvasCompositionMode;
    
    modifiedDate: Date;
    statusUpdateDate: Date;
    _draft?: Project;
    deviceOwnerId: string;
    readonly syncStatus: SyncStatus;

    constructor(id: string, 
        title: string, 
        createDate: Date, 
        modifiedDate: Date, 
        size: { width: number, height: number }, 
        canvasSizeIsCircular: boolean, 
        duration: number, 
        scenes: number, 
        customFonts: string[], 
        minVersion: number, 
        updateDeviceId: string, 
        statusUpdateDate: Date, 
        status: SyncStatus, 
        deviceOwner: string, 
        isInConflict: boolean, 
        screenshotUrl?: string, 
        features: SupportedFeature[] = [],
        canvasComposition: CanvasCompositionMode = CanvasCompositionMode.PAGES) {
        this.draftId = id;
        this.title = title;
        this.createDate = createDate;
        this.modifiedDate = modifiedDate;
        this.size = size;
        this.duration = duration;
        this.scenes = scenes;
        this.customFonts = customFonts;
        this.minVersion = minVersion;
        this.syncStatus = status;
        this.updateDeviceId = updateDeviceId;
        this.statusUpdateDate = statusUpdateDate;
        this.deviceOwnerId = deviceOwner;
        this.canvasSizeIsCircular = canvasSizeIsCircular;
        this.isInConflict = isInConflict;
        this.screenshotUrl = screenshotUrl;
        this.features = features;
        this.canvasComposition = canvasComposition;
    }

     static fromDraftNoScreenshot(draft: Project): DraftSyncVersion {
        let draftConfig = draft.templateConfig;
        const duration = draftConfig.scenes?.reduce((acc, scene) => acc + scene.duration, 0);
        const deviceId = UserDevice.current.id;
        const isCircular = draftConfig.canvasSizeIsCircular;
        let modificationDateOptional = draftConfig.draftDate || draftConfig.draftCreationDate;
        const modificationDate = (modificationDateOptional instanceof Date) ? modificationDateOptional : new Date(modificationDateOptional);

        const customFonts = draftConfig.fonts?.filter(font => font?.isCustomFont)
            .map(font => font.fileURL.split('/').pop() || "");

        const draftSyncVersion = new DraftSyncVersion(
            draftConfig.draftGuid,
            draftConfig.draftTitle,
            draftConfig.draftCreationDate,
            modificationDate,
            draftConfig.originalCanvasSize,
            isCircular,
            duration,
            draftConfig.scenes?.length ?? 0,
            customFonts,
            draftConfig.minDraftVersion,
            deviceId,
            DateProvider.getUTCDate(),
            SyncStatus.None,
            deviceId,
            false,
            null,
            draft.features ?? [],
            CanvasCompositionMode.PAGES
        );

        draftSyncVersion._draft = reviveWithProto<Project>(Project, draft)
        return draftSyncVersion;
    }

    static async fromDraft(draft: Project): Promise<DraftSyncVersion> {
        let draftSyncVersion = await DraftSyncVersion.fromDraftNoScreenshot(draft);
        let screenshotUrl = await draft.screenshotInBlobUrl();
        draftSyncVersion.screenshotUrl = screenshotUrl;
        return draftSyncVersion;
    }

    get isLocal(): boolean {
        return !!this._draft || false;
    }

    get isCurrentDeviceVersion(): boolean {
        return this.isLocal || this.deviceOwnerId === UserDevice.current.id;
    }

    setSyncStatus(status: SyncStatus) {
        // @ts-ignore
        this.syncStatus = status;
        this.statusUpdateDate = DateProvider.getUTCDate();
        this.deviceOwnerId = UserDevice.current.id;
    }

    isSupportedOnWeb(): boolean {
        if (!this._draft) {
            let webFeatures = SupportedFeatureOnWebValues
            return this.features.every(f => webFeatures.includes(f));
        }
        return this._draft.isSupportedOnWeb();
    }

    static withDraft(draft: TemplateConfig): DraftSyncVersion {
        const duration = draft.scenes?.reduce((acc: number, scene: any) => acc + scene.duration, 0);
        const deviceId = UserDevice.current.id;
        const isCircular = draft.canvasSizeIsCircular;
        const modificationDate = draft.draftDate || draft.draftCreationDate;
        const customFonts = draft.fonts?.filter((font: any) => font?.isCustomFont).map((font: any) => font?.fileURL?.split('/').pop());
        const draftFeatures = (draft as TemplateConfig & { features?: SupportedFeature[] }).features ?? [];
        return new DraftSyncVersion(draft.draftGuid, draft.draftTitle, draft.draftCreationDate, modificationDate, { width: draft.originalCanvasSize.width, height: draft.originalCanvasSize.height }, isCircular, duration, draft.scenes?.length ?? 0, customFonts, draft.minDraftVersion, deviceId,  DateProvider.getUTCDate(), SyncStatus.None, deviceId, false, undefined, draftFeatures, CanvasCompositionMode.PAGES);
    }

    deviceCloudVersionCopy(): DraftSyncVersion {
        return new DraftSyncVersion(this.draftId, this.title, this.createDate, this.modifiedDate, this.size, this.canvasSizeIsCircular, this.duration, this.scenes, this.customFonts, this.minVersion, this.updateDeviceId, this.statusUpdateDate, this.syncStatus, UserDevice.current.id, this.isInConflict, this.screenshotUrl, this.features, this.canvasComposition);
    }

    get roundedTimestamp(): number {
        try {
            return this.modifiedDate.getTime();
        } catch {
            return 0;
        }
    }

    static compare(lhs: DraftSyncVersion, rhs: DraftSyncVersion): boolean {
        if (lhs.roundedTimestamp === rhs.roundedTimestamp) {
            return rhs.isCurrentDeviceVersion && !lhs.isCurrentDeviceVersion;
        }
        return lhs.roundedTimestamp < rhs.roundedTimestamp;
    }

    isWaitingForData(): boolean {
        return (this.syncStatus !== SyncStatus.Updated) && (this.syncStatus !== SyncStatus.Uploading && this.deviceOwnerId == 'web');
    }

    // Method to convert the instance to JSON
    toJSON(): any {
        return {
            draftId: this.draftId,
            createDate: this.createDate.toISOString(),
            modifiedDate: this.modifiedDate.toISOString(),
            size: this.size,
            canvasSizeIsCircular: this.canvasSizeIsCircular,
            duration: this.duration,
            scenes: this.scenes,
            minVersion: this.minVersion,
            title: this.title,
            customFonts: this.customFonts,
            isInConflict: this.isInConflict,
            updateDeviceId: this.updateDeviceId,
            screenshotUrl: this.screenshotUrl && this.screenshotUrl.indexOf('data:image/') !== 0 ? this.screenshotUrl.toString() : undefined,
            syncStatus: this.syncStatus,
            statusUpdateDate: this.statusUpdateDate.toISOString(),
            deviceOwnerId: this.deviceOwnerId,
            features: this.features ?? [],
            canvasComposition: this.canvasComposition
        };
    }

    // Static method to create an instance from JSON
    static fromJSON(data: any): DraftSyncVersion {
        const json = (typeof data === 'string') ? JSON.parse(data) : data;

        let result = new DraftSyncVersion(
            json.draftId,
            json.title,
            new Date(json.createDate),
            new Date(json.modifiedDate),
            json.size,
            json.canvasSizeIsCircular,
            json.duration,
            json.scenes,
            json.customFonts,
            json.minVersion,
            json.updateDeviceId,
            new Date(json.statusUpdateDate),
            json.syncStatus,
            json.deviceOwnerId,
            json.isInConflict,
            json.screenshotUrl ? json.screenshotUrl : undefined,
            json.features ? json.features : [],
            json.canvasComposition ?? CanvasCompositionMode.PAGES
        );
        
        if (json._draft) {
            result._draft = Project.fromJSON(json._draft);
        } else {
            result._draft = undefined;
        }

        return result;
    }
}